import React from 'react';
import { Zap, Clock, FolderOpen, Cpu, Share2, ExternalLink } from 'lucide-react';
import type { SiteTaskDetail } from '../../redux/api/tasksApi';
import { SEVERITY_BADGE_STYLES, STATUS_BADGE_STYLES, componentChipClass } from '../../constants/eventStyles';

interface TaskDetailContentProps {
  detail: SiteTaskDetail;
  onEventClick: (eventId: string) => void;
  onComponentClick: (component: string) => void;
  activeComponent: string;
}

function TaskDetailContent({ detail, onEventClick, onComponentClick, activeComponent }: TaskDetailContentProps) {
  return (
    <div className="space-y-3 py-2">
      {detail.source_event && (
        <div>
          <h4 className="text-[10px] font-semibold uppercase text-[var(--color-text-muted)] tracking-wide mb-1">Source Event</h4>
          <button
            onClick={() => onEventClick(detail.source_event!.id)}
            className="w-full text-left p-2 rounded bg-[var(--color-bg-tertiary)] hover:bg-[var(--color-surface-hover)] transition-colors"
          >
            <div className="flex items-center gap-2 flex-wrap">
              <Zap className="w-3 h-3 text-orange-500 shrink-0" />
              <span className="text-xs font-medium text-[var(--color-text-primary)]">{detail.source_event.title}</span>
              <span className={`px-1.5 py-0.5 text-[10px] font-semibold rounded ${SEVERITY_BADGE_STYLES[detail.source_event.severity] || ''}`}>
                {detail.source_event.severity}
              </span>
              <span className="px-1.5 py-0.5 text-[10px] rounded border border-[var(--color-border)] text-[var(--color-text-muted)]">
                {detail.source_event.event_type}
              </span>
            </div>
            <div className="flex items-center gap-3 mt-1 text-[11px] text-[var(--color-text-muted)]">
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {new Date(detail.source_event.occurred_at).toLocaleString()}
              </span>
              <span className={`px-1.5 py-0.5 text-[10px] rounded ${STATUS_BADGE_STYLES[detail.source_event.status] || ''}`}>
                {detail.source_event.status.replace('_', ' ')}
              </span>
            </div>
          </button>
        </div>
      )}

      {detail.linked_events && detail.linked_events.length > 0 && (
        <div>
          <h4 className="text-[10px] font-semibold uppercase text-[var(--color-text-muted)] tracking-wide mb-1">
            Linked Events ({detail.linked_events.length})
          </h4>
          <div className="space-y-1">
            {detail.linked_events.map((evt) => (
              <button
                key={evt.id}
                onClick={() => onEventClick(evt.id)}
                className="w-full text-left p-2 rounded bg-[var(--color-bg-tertiary)] hover:bg-[var(--color-surface-hover)] transition-colors flex items-center gap-2"
              >
                <Zap className="w-3 h-3 text-[var(--color-text-muted)] shrink-0" />
                <span className="text-xs text-[var(--color-text-secondary)] truncate flex-1">{evt.title}</span>
                <span className={`shrink-0 px-1.5 py-0.5 text-[10px] font-semibold rounded ${SEVERITY_BADGE_STYLES[evt.severity] || ''}`}>
                  {evt.severity}
                </span>
                <span className="shrink-0 text-[10px] text-[var(--color-text-muted)]">
                  {new Date(evt.occurred_at).toLocaleDateString()}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {detail.entity_metadata && detail.entity_metadata.affected_entities.length > 0 && (
        <div>
          <h4 className="text-[10px] font-semibold uppercase text-[var(--color-text-muted)] tracking-wide mb-1">
            Affected Equipment
          </h4>
          {(detail.entity_metadata.total_capacity_affected_kw || detail.entity_metadata.total_energy_impact_kwh) && (
            <div className="flex gap-2 mb-2 text-[11px]">
              {detail.entity_metadata.total_capacity_affected_kw != null && (
                <span className="px-2 py-0.5 rounded text-orange-600" style={{ backgroundColor: 'rgba(251, 146, 60, 0.12)' }}>
                  {detail.entity_metadata.total_capacity_affected_kw} kW affected
                </span>
              )}
              {detail.entity_metadata.total_energy_impact_kwh != null && (
                <span className="px-2 py-0.5 rounded text-red-500" style={{ backgroundColor: 'rgba(239, 68, 68, 0.12)' }}>
                  {detail.entity_metadata.total_energy_impact_kwh.toFixed(1)} kWh impact
                </span>
              )}
            </div>
          )}
          <div className="space-y-1">
            {detail.entity_metadata.affected_entities.map((entity, idx) => (
              <div key={idx} className="flex items-center gap-2 p-1.5 rounded bg-[var(--color-bg-tertiary)] text-xs">
                <span className="px-1.5 py-0.5 rounded border border-[var(--color-border)] text-[10px] text-[var(--color-text-muted)]">
                  {entity.entity_type}
                </span>
                <button
                  onClick={() => onComponentClick(entity.entity_identifier)}
                  className={componentChipClass(activeComponent === entity.entity_identifier)}
                  title={`Filter by ${entity.entity_identifier}`}
                >
                  <Cpu className="w-3 h-3" />
                  {entity.entity_identifier}
                </button>
                {entity.asset ? (
                  <span className="px-1.5 py-0.5 text-[10px] rounded text-emerald-500" style={{ backgroundColor: 'rgba(16, 185, 129, 0.12)' }}>
                    {entity.asset.name || entity.asset.asset_tag || 'Resolved'}
                  </span>
                ) : (
                  <span className="px-1.5 py-0.5 text-[10px] rounded text-red-500" style={{ backgroundColor: 'rgba(239, 68, 68, 0.12)' }}>
                    Unresolved
                  </span>
                )}
                {entity.role && (
                  <span className="text-[var(--color-text-muted)]">{entity.role}</span>
                )}
                {entity.capacity_kw != null && (
                  <span className="text-[var(--color-text-muted)] ml-auto">{entity.capacity_kw} kW</span>
                )}
              </div>
            ))}
          </div>
          <button
            onClick={() => {
              const identifiers = detail.entity_metadata!.affected_entities.map(e => e.entity_identifier);
              window.dispatchEvent(new CustomEvent('openTab', { detail: { tabType: 'networkviewer' } }));
              window.dispatchEvent(new CustomEvent('navigateToNetworkNode', {
                detail: { entityIdentifiers: identifiers, siteId: detail.site_id }
              }));
            }}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 mt-2 text-xs font-medium rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-tertiary)] hover:bg-[var(--color-surface-hover)] hover:border-[#17c480]/40 transition-colors"
          >
            <Share2 className="w-3.5 h-3.5 text-[#17c480]" />
            <span className="text-[var(--color-text-primary)]">View in Network</span>
            <ExternalLink className="w-3 h-3 text-[var(--color-text-muted)] ml-auto" />
          </button>
        </div>
      )}

      {detail.task_group && (
        <div>
          <h4 className="text-[10px] font-semibold uppercase text-[var(--color-text-muted)] tracking-wide mb-1">Task Group</h4>
          <div className="p-2 rounded bg-[var(--color-bg-tertiary)] flex items-center gap-2 text-xs">
            <FolderOpen className="w-3 h-3 text-[var(--color-text-muted)]" />
            <span className="font-medium text-[var(--color-text-primary)]">{detail.task_group.name}</span>
            <span className="px-1.5 py-0.5 rounded border border-[var(--color-border)] text-[10px] text-[var(--color-text-muted)]">
              {detail.task_group.group_type}
            </span>
            <span className={`px-1.5 py-0.5 text-[10px] rounded ${STATUS_BADGE_STYLES[detail.task_group.status] || ''}`}>
              {detail.task_group.status}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

export default TaskDetailContent;
